import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';

/**
 * useAjax
 * 
 * Generic AJAX hook for making calls to WordPress AJAX endpoints.
 * Handles:
 * - withCredentials for session/cookies
 * - Nonce validation
 * - Session token (for Safari mobile)
 * - Error handling
 */
export function useAjax() {
  const { nonce, session_token } = useAuth();
  const { api_base } = useConfig();

  const request = async (action, data = {}, options = {}) => {
    const {
      method = 'POST',
      retries = 1,
      timeout = 30000,
    } = options;

    const url = new URL(api_base, window.location.origin);
    url.searchParams.set('action', action);

    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;

    const headers = isFormData
      ? {}
      : {
          'Content-Type': 'application/x-www-form-urlencoded',
        };

    let body;
    if (isFormData) {
      const formData = data;
      if (!formData.has('nonce')) {
        formData.append('nonce', nonce);
      }
      if (session_token && !formData.has('sess')) {
        formData.append('sess', session_token);
      }
      body = method !== 'GET' ? formData : undefined;
      if (method === 'GET') {
        formData.forEach((value, key) => url.searchParams.set(key, value));
      }
    } else {
      const params = new URLSearchParams();
      params.append('nonce', nonce);
      if (session_token) {
        params.append('sess', session_token);
      }
      Object.entries(data).forEach(([key, value]) => {
        params.append(key, value ?? '');
      });
      // For GET requests, move params into URL query string (browsers drop GET bodies)
      if (method === 'GET') {
        params.forEach((value, key) => url.searchParams.set(key, value));
      }
      body = method !== 'GET' ? params.toString() : undefined;
    }

    let lastError;
    for (let i = 0; i < retries; i++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(url.toString(), {
          method,
          body: method !== 'GET' ? body : undefined,
          credentials: 'include',
          headers,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const json = await response.json();

        if (json.success === false) {
          throw new Error(json.data?.message || json.data?.msg || 'AJAX request failed');
        }

        return json.data;
      } catch (err) {
        lastError = err;
        if (i < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000));
        }
      }
    }

    throw lastError;
  };

  return { request };
}
